/**
 * Money as the payments backend speaks it: Gam in whole coins, fiat in minor units (cents).
 * `formatMoney` is the single place that turns either into a user-facing price string.
 */
const PURCHASE_CURRENCIES = ['gam', 'usd'] as const

export type PurchaseCurrency = (typeof PURCHASE_CURRENCIES)[number]

export interface Money {
    amount: number
    currency: PurchaseCurrency
}

export const GAM_CURRENCY_LABEL = 'Gam'
const FIAT_MINOR_UNITS = 100

export function normalizePurchaseCurrency(value: unknown): PurchaseCurrency | undefined {
    const normalized = typeof value === 'string' ? value.toLowerCase() : undefined
    return (PURCHASE_CURRENCIES as readonly string[]).includes(normalized ?? '')
        ? (normalized as PurchaseCurrency)
        : undefined
}

export function toMajorUnits({ amount, currency }: Money) {
    return currency === 'gam' ? amount : amount / FIAT_MINOR_UNITS
}

export function formatMoney(money: Money): string {
    if (money.currency === 'gam') {
        return `${money.amount} ${GAM_CURRENCY_LABEL}`
    }

    const currency = money.currency.toUpperCase()
    const value = toMajorUnits(money)
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value)
    } catch {
        return `${value.toFixed(2)} ${currency}`
    }
}
